import { useCallback, useMemo } from 'react';
import type {
  NetworkCostsQuery,
  ReconciliationCasesQuery,
  ReconciliationRunRowsQuery,
  ReconciliationRunsQuery,
  SuspenseListQuery,
} from '@shared/api/contracts';
import { swrKeys } from '@shared/api/swr/keys';
import { useApiServices } from '@shared/lib/use-api-services';
import { RECONCILIATION_LIST_PAGE_SIZE, serializeReconciliationQuery } from '../lib/reconciliation-query';
import { useReconciliationCursorList } from './use-reconciliation-cursor-list';

const withLimit = <T extends { limit?: number }>(query: T): T => ({
  ...query,
  limit: query.limit ?? RECONCILIATION_LIST_PAGE_SIZE,
});

export function useReconciliationRunsList(query: ReconciliationRunsQuery, page: number, enabled: boolean) {
  const services = useApiServices();
  const params = useMemo(() => withLimit(query), [query]);
  const limit = params.limit ?? RECONCILIATION_LIST_PAGE_SIZE;
  const signature = serializeReconciliationQuery(params as Record<string, unknown>);
  const fetchPage = useCallback(
    (cursor?: string) => services.reconciliation.listRuns({ ...params, cursor }),
    [params, services.reconciliation],
  );
  return useReconciliationCursorList(
    enabled,
    swrKeys.reconciliation.runs()[0],
    signature,
    page,
    limit,
    fetchPage,
  );
}

export function useReconciliationDiscrepanciesList(
  query: ReconciliationCasesQuery,
  page: number,
  enabled: boolean,
) {
  const services = useApiServices();
  const params = useMemo(() => withLimit(query), [query]);
  const limit = params.limit ?? RECONCILIATION_LIST_PAGE_SIZE;
  const signature = serializeReconciliationQuery(params as Record<string, unknown>);
  const fetchPage = useCallback(
    (cursor?: string) => services.reconciliation.listDiscrepancies({ ...params, cursor }),
    [params, services.reconciliation],
  );
  return useReconciliationCursorList(
    enabled,
    swrKeys.reconciliation.discrepancies()[0],
    signature,
    page,
    limit,
    fetchPage,
  );
}

export function useReconciliationSuspenseList(query: SuspenseListQuery, page: number, enabled: boolean) {
  const services = useApiServices();
  const params = useMemo(() => withLimit(query), [query]);
  const limit = params.limit ?? RECONCILIATION_LIST_PAGE_SIZE;
  const signature = serializeReconciliationQuery(params as Record<string, unknown>);
  const fetchPage = useCallback(
    (cursor?: string) => services.reconciliation.listSuspense({ ...params, cursor }),
    [params, services.reconciliation],
  );
  return useReconciliationCursorList(
    enabled,
    swrKeys.reconciliation.suspense()[0],
    signature,
    page,
    limit,
    fetchPage,
  );
}

export function useReconciliationNetworkCostsList(query: NetworkCostsQuery, page: number, enabled: boolean) {
  const services = useApiServices();
  const params = useMemo(() => withLimit(query), [query]);
  const limit = params.limit ?? RECONCILIATION_LIST_PAGE_SIZE;
  const signature = serializeReconciliationQuery(params as Record<string, unknown>);
  const fetchPage = useCallback(
    (cursor?: string) => services.reconciliation.listNetworkCosts({ ...params, cursor }),
    [params, services.reconciliation],
  );
  return useReconciliationCursorList(
    enabled,
    swrKeys.reconciliation.networkCosts()[0],
    signature,
    page,
    limit,
    fetchPage,
  );
}

export function useReconciliationRunRowsList(
  runId: string | null,
  query: ReconciliationRunRowsQuery,
  page: number,
  enabled: boolean,
) {
  const services = useApiServices();
  const params = useMemo(() => withLimit(query), [query]);
  const limit = params.limit ?? RECONCILIATION_LIST_PAGE_SIZE;
  const signature = serializeReconciliationQuery(params as Record<string, unknown>);
  const fetchPage = useCallback(
    (cursor?: string) => {
      if (!runId) return Promise.resolve({ data: [], nextCursor: null });
      return services.reconciliation.listRunRows(runId, { ...params, cursor });
    },
    [params, runId, services.reconciliation],
  );
  return useReconciliationCursorList(
    enabled && Boolean(runId),
    runId ? `${swrKeys.reconciliation.run(runId)}/rows` : 'reconciliation-run-rows',
    signature,
    page,
    limit,
    fetchPage,
  );
}
